import React, { useEffect } from 'react';
import { DarkTheme, LightTheme } from './glueTheme';

// Convert hex color to HSL values for CSS variables
function hexToHsl(hex: string): string {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number, s: number, l: number;

    l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                h = 0;
        }
        h /= 6;
    }

    // Convert to percentage and degrees
    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return `${h} ${s}% ${l}%`;
}

// Handle named colors and special cases
function colorToHsl(color: string): string {
    if (color === 'white') return '0 0% 100%';
    if (color === 'black') return '0 0% 0%';
    if (color.startsWith('#')) return hexToHsl(color);

    // For other named colors, create a temporary element to get computed color
    const tempEl = document.createElement('div');
    tempEl.style.color = color;
    document.body.appendChild(tempEl);
    const computedColor = getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    // Convert rgb() to hex then to HSL
    const match = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
        const hex =
            '#' +
            parseInt(match[1]).toString(16).padStart(2, '0') +
            parseInt(match[2]).toString(16).padStart(2, '0') +
            parseInt(match[3]).toString(16).padStart(2, '0');
        return hexToHsl(hex);
    }

    // Fallback
    return '0 0% 50%';
}

// Map Glue theme colors to CSS variables
function updateCSSVariables(theme: typeof LightTheme) {
    const root = document.documentElement;

    // Map Glue colors to shadcn/ui CSS variables
    root.style.setProperty('--background', colorToHsl(theme.backgroundColor));
    root.style.setProperty('--foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--card', colorToHsl(theme.elevatedBackgroundColor));
    root.style.setProperty('--card-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--popover', colorToHsl(theme.elevatedBackgroundColor));
    root.style.setProperty('--popover-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--primary', colorToHsl(theme.positiveAccentColor));
    root.style.setProperty('--primary-foreground', colorToHsl(theme.primaryColor));
    root.style.setProperty('--secondary', colorToHsl(theme.subtleAccentColor));
    root.style.setProperty('--secondary-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--muted', colorToHsl(theme.subtleAccentColor));
    root.style.setProperty('--muted-foreground', colorToHsl(theme.disabledText));
    root.style.setProperty('--accent', colorToHsl(theme.subtleAccentColor));
    root.style.setProperty('--accent-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--destructive', colorToHsl(theme.negativeAccentColor));
    root.style.setProperty('--destructive-foreground', colorToHsl(theme.primaryColor));
    root.style.setProperty('--border', colorToHsl(theme.separatorColor));
    root.style.setProperty('--input', colorToHsl(theme.separatorColor));
    root.style.setProperty('--ring', colorToHsl(theme.positiveAccentColor));

    // Sidebar CSS variables
    root.style.setProperty('--sidebar-background', colorToHsl(theme.elevatedBackgroundColor));
    root.style.setProperty('--sidebar-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--sidebar-primary', colorToHsl(theme.positiveAccentColor));
    root.style.setProperty('--sidebar-primary-foreground', colorToHsl(theme.primaryColor));
    root.style.setProperty('--sidebar-accent', colorToHsl(theme.subtleAccentColor));
    root.style.setProperty('--sidebar-accent-foreground', colorToHsl(theme.textColor));
    root.style.setProperty('--sidebar-border', colorToHsl(theme.separatorColor));
    root.style.setProperty('--sidebar-ring', colorToHsl(theme.positiveAccentColor));

    // Add or remove dark class for tailwind dark mode
    if (theme === DarkTheme) {
        root.classList.add('dark');
    } else {
        root.classList.remove('dark');
    }
}

interface ThemeManagerProps {
    theme: typeof LightTheme;
}

export const ThemeManager: React.FC<ThemeManagerProps> = ({ theme }) => {
    useEffect(() => {
        updateCSSVariables(theme);
    }, [theme]);

    return null; // This component doesn't render anything
};
